import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["todo","in_progress","review","done"];
const PRIORITIES = ["low","medium","high"];

const priorityColor = { high: "bg-primary/15 text-primary", medium: "bg-accent/20 text-accent", low: "bg-muted text-muted-foreground" };

const emptyForm = { title: "", project_id: "", priority: "medium", deadline: "", description: "", assigned_to: "" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [filterProject, setFilterProject] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/tasks").then((r) => setTasks(r.data));
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/employees").then((r) => setEmployees(r.data));
  }, []);

  const add = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      await api.post("/tasks", { ...form, status: "todo" });
      load();
      setForm(emptyForm);
      toast.success("Task added");
    } catch { toast.error("Failed"); }
  };
  const update = async (t, patch) => { await api.patch(`/tasks/${t.id}`, patch); load(); };
  const remove = async (id) => { if (!window.confirm("Delete task?")) return; await api.delete(`/tasks/${id}`); load(); };

  const openEdit = (t) => {
    setEditForm({
      title: t.title || "", project_id: t.project_id || "", priority: t.priority || "medium",
      deadline: t.deadline || "", description: t.description || "", assigned_to: t.assigned_to || "",
    });
    setEditId(t.id); setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editForm.title) return toast.error("Title required");
    try { await api.patch(`/tasks/${editId}`, editForm); setEditOpen(false); load(); toast.success("Task updated"); }
    catch { toast.error("Failed"); }
  };

  const empName = (id) => employees.find(e => e.id === id)?.name || "";
  const projName = (id) => projects.find(p => p.id === id)?.name || "";

  const filtered = tasks.filter(t =>
    (filterProject === "all" || t.project_id === filterProject) &&
    (filterAssignee === "all" || t.assigned_to === filterAssignee)
  );

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Execution</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">Tasks</h1>
      </div>

      {/* Create form */}
      <div className="rounded-2xl p-4 bg-card border border-border/60 grid grid-cols-1 md:grid-cols-6 gap-2" data-testid="task-create-form">
        <Input placeholder="Task title…" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="md:col-span-2" data-testid="task-title-input" />
        <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
          <SelectTrigger data-testid="task-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
          <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline-input" />
        <Textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="md:col-span-5" data-testid="task-description-input" />
        <Button onClick={add} data-testid="task-add-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Filter by</span>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-52 h-8" data-testid="filter-project"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="w-52 h-8" data-testid="filter-assignee"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {STATUSES.map((s) => (
          <div key={s} className="rounded-xl bg-muted/30 border border-border/50 p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground px-1 pb-3 capitalize flex justify-between">
              <span>{s.replace("_"," ")}</span>
              <span>{filtered.filter((t) => t.status === s).length}</span>
            </div>
            <div className="space-y-2">
              {filtered.filter((t) => t.status === s).map((t) => (
                <div key={t.id} data-testid={`task-${t.id}`} className="kanban-card rounded-lg bg-card border border-border/50 p-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="text-sm font-medium flex-1">{t.title}</div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(t.id)} data-testid={`delete-task-${t.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  {t.description && <div className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{t.description}</div>}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${priorityColor[t.priority]}`}>{t.priority}</span>
                    {t.deadline && <span className="text-[10px] text-muted-foreground">{t.deadline}</span>}
                    {t.assigned_to && <span className="text-[10px] text-muted-foreground">· {empName(t.assigned_to)}</span>}
                  </div>
                  {t.project_id && <div className="mt-1 text-[10px] text-muted-foreground truncate">{projName(t.project_id)}</div>}
                  <div className="mt-2">
                    <Select value={t.status} onValueChange={(v) => update(t, { status: v })}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((x) => <SelectItem key={x} value={x} className="capitalize">{x.replace("_"," ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {t.progress > 0 && <Progress value={t.progress} className="h-1 mt-2" />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Task</DialogTitle><DialogDescription>Update title, project, assignee, priority, deadline and description.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Input className="col-span-2" placeholder="Title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-task-title" />
            <Select value={editForm.project_id} onValueChange={(v) => setEditForm({ ...editForm, project_id: v })}>
              <SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={editForm.assigned_to} onValueChange={(v) => setEditForm({ ...editForm, assigned_to: v })}>
              <SelectTrigger><SelectValue placeholder="Assignee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={editForm.deadline} onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })} />
            <Textarea className="col-span-2" placeholder="Description" rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <Button onClick={saveEdit} data-testid="save-task-edit-btn" className="bg-primary text-primary-foreground">Update Task</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
